import {z} from "zod/v4-mini"
import {mapFields} from "@cm/utils"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {GeometryAttributeData, IndexingDim} from "#template/nodes/geometry-graph/interfaces"
import {GeometryAttributeNode} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {GeomBuilderContext} from "#template/geometry-processing/geometry-graph"
import type {ISceneManager} from "#template/interfaces/scene-manager"
import type {EvaluableGeometryGeneratorArgs, GeometryGenerator, GeometryGeneratorArg} from "#template/nodes/geometry-graph/lambda-types"
import {
    evaluableGeometryGeneratorArgs,
    generateGeometryLambdaRuntimeNode,
    geometryGenerator,
    getHashForGeometryGeneratorArgs,
} from "#template/nodes/geometry-graph/lambda-types"

export type AttributeLambdaParameters<T extends IndexingDim> = {
    name: string
    args: EvaluableGeometryGeneratorArgs | null
    generator: GeometryGenerator<GeometryAttributeData<T>> | null
}

export const attributeLambdaParametersSchema = <T extends IndexingDim>() =>
    z.object({
        name: z.string(),
        args: z.nullable(evaluableGeometryGeneratorArgs),
        generator: z.nullable(geometryGenerator<GeometryAttributeData<T>>()),
    })

// One shared parameterized base — variants pin `T` and declare their dim-specific marker manually.
// Note: unlike sibling operator bases (cast/binary-math/...), lambda has no `TAttribute` slot — its
// inputs are the opaque `args` record and `generator` closure, so only the dim is generic.
export abstract class GeometryAttributeLambdaNode<
    out T extends IndexingDim,
    out P extends AttributeLambdaParameters<T> = AttributeLambdaParameters<T>,
> extends GeometryAttributeNode<T, P> {
    protected override hasValidParameters(parameters: P): parameters is P & NonNullableParameters<P> {
        return parameters.generator !== null && parameters.args !== null
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<P>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
        ctx: GeomBuilderContext,
        sceneManager: ISceneManager,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeData<T> | null>; invalidator?: BuilderInlet<null>} {
        const {args, generator} = parameters

        const evaluatedArgs = scope.record<string, GeometryGeneratorArg>(
            mapFields(args, (value) => evaluator.evaluate(value) as BuilderInlet<GeometryGeneratorArg>),
        )
        const evaluatedArgsWithHash = scope.pureLambda(
            evaluatedArgs,
            (evaluatedArgs) => ({
                args: evaluatedArgs,
                hash: getHashForGeometryGeneratorArgs(evaluatedArgs),
            }),
            "GeometryGeneratorArgsWithHash",
        )

        const lambdaRuntimeNodeClass = generateGeometryLambdaRuntimeNode<GeometryAttributeData<T>>(this.nodeClass)

        const {output} = scope.node(lambdaRuntimeNodeClass, {
            sceneManager,
            geomBuilderContext: ctx,
            argsWithHash: evaluatedArgsWithHash,
            generator,
        })

        const geometryAttributeData = scope.get(output, "output")
        return {evaluatedGeometryData: geometryAttributeData}
    }
}
